import { useState, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InvestmentRecord {
  contributionId: string;
  goalId: string;
  title: string;
  imageUrl: string;
  goalAmount: number;
  currentAmount: number;
  investmentAmount: number;
  ownershipPercent: number;
  status: 'active' | 'completed' | 'pending';
  createdAt: Date;
  coInvestors: { name: string | null; amount: number }[];
}

export interface AngelBadge {
  badge_tier: 'friend' | 'family' | 'fam' | 'legend';
  contribution_count: number;
  total_contributed: number;
  show_details: boolean;
}

export interface GiftRecord {
  contributionId: string;
  goalId: string;
  title: string;
  imageUrl: string;
  goalAmount: number;
  currentAmount: number;
  giftAmount: number;
  status: 'active' | 'completed' | 'pending';
  createdAt: Date;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useInvestorPortfolio(userId: string) {
  const [investments, setInvestments] = useState<InvestmentRecord[]>([]);
  const [gifts, setGifts] = useState<GiftRecord[]>([]);
  const [angelBadge, setAngelBadge] = useState<AngelBadge | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      // Seed investor has no real data — skip fetch
      if (!userId || userId === 'user_seed_investor_1') {
        if (!cancelled) { setLoading(false); }
        return;
      }

      try {
        const resp = await fetch(`/api/investor-portfolio?userId=${encodeURIComponent(userId)}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json() as {
          investments: Array<Record<string, unknown>>;
          gifts: Array<Record<string, unknown>>;
          angel_badge: Record<string, unknown> | null;
        };

        if (!cancelled) {
          setInvestments(
            (data.investments ?? []).map((row) => ({
              contributionId: String(row.contribution_id ?? ''),
              goalId: String(row.goal_id ?? ''),
              title: String(row.title ?? ''),
              imageUrl: String(row.image_url ?? ''),
              goalAmount: Number(row.goal_amount ?? 0),
              currentAmount: Number(row.current_amount ?? 0),
              investmentAmount: Number(row.investment_amount ?? 0),
              ownershipPercent: Number(row.ownership_percentage ?? 0),
              status: (['active', 'completed', 'pending'].includes(String(row.status))
                ? row.status
                : 'active') as InvestmentRecord['status'],
              createdAt: new Date(row.created_at as string),
              coInvestors: (row.co_investors as Array<{ name: string | null; amount: number }> ?? []),
            }))
          );
          setGifts(
            (data.gifts ?? []).map((row) => ({
              contributionId: String(row.contribution_id ?? ''),
              goalId: String(row.goal_id ?? ''),
              title: String(row.title ?? ''),
              imageUrl: String(row.image_url ?? ''),
              goalAmount: Number(row.goal_amount ?? 0),
              currentAmount: Number(row.current_amount ?? 0),
              giftAmount: Number(row.gift_amount ?? 0),
              status: (['active', 'completed', 'pending'].includes(String(row.status))
                ? row.status
                : 'active') as GiftRecord['status'],
              createdAt: new Date(row.created_at as string),
            }))
          );
          if (data.angel_badge) {
            const ab = data.angel_badge;
            setAngelBadge({
              badge_tier: ab.badge_tier as AngelBadge['badge_tier'],
              contribution_count: Number(ab.contribution_count ?? 0),
              total_contributed: Number(ab.total_contributed ?? 0),
              show_details: Boolean(ab.show_details),
            });
          } else {
            setAngelBadge(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load portfolio');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [userId]);

  return { investments, gifts, angelBadge, loading, error };
}
